import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Materialentnahme, Projekt, Mitarbeiter, Artikel, Auftrag, AuftragKurz } from '../types';
import AktivitaetenBlock from './AktivitaetenBlock';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const MaterialentnahmeList: React.FC = () => {
  const [entnahmen, setEntnahmen] = useState<Materialentnahme[]>([]);
  const [projekte, setProjekte] = useState<Projekt[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<Mitarbeiter[]>([]);
  const [artikelListe, setArtikelListe] = useState<Artikel[]>([]);
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    ziel_typ: 'auftrag' as 'auftrag' | 'projekt',
    auftrag_id: '',
    projekt_id: '',
    artikel_id: '',
    mitarbeiter_id: '',
    menge: '',
    lagerplatz: '',
    notiz: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [kandidaten, setKandidaten] = useState<AuftragKurz[]>([]);
  const [warnung, setWarnung] = useState('');
  const [akteId, setAkteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get<Materialentnahme[]>(`${API_URL}/api/materialentnahmen`),
      axios.get<Projekt[]>(`${API_URL}/api/projekte`),
      axios.get<Mitarbeiter[]>(`${API_URL}/api/mitarbeiter`),
      axios.get<Artikel[]>(`${API_URL}/api/artikel`),
      axios.get<Auftrag[]>(`${API_URL}/api/auftraege`),
    ]).then(([eRes, pRes, mRes, aRes, auRes]) => {
      setEntnahmen(eRes.data);
      setProjekte(pRes.data);
      setMitarbeiter(mRes.data);
      setArtikelListe(aRes.data);
      setAuftraege(auRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Nur Aufträge, auf die laut C-Regel (Spec 2026-08-28) noch gebucht werden
  // darf — Storniert/Fakturiert sind Endstatus für Aufwandsbuchungen.
  const buchbareAuftraege = auftraege.filter(a => a.status !== 'Storniert' && a.status !== 'Fakturiert');

  // Gemeinsamer POST-Pfad für den normalen Formular-Submit UND den erneuten
  // Versuch nach Klick auf einen Kandidaten aus der 409-Liste — beide senden
  // dieselben übrigen Formularfelder, nur die Zieladresse unterscheidet sich.
  const buchen = async (auftragId: number | null, projektId: number | null) => {
    setError('');
    setWarnung('');
    setKandidaten([]);
    setSaving(true);
    try {
      const res = await axios.post<Materialentnahme>(`${API_URL}/api/materialentnahmen`, {
        auftrag_id: auftragId ?? undefined,
        projekt_id: projektId ?? undefined,
        artikel_id: parseInt(form.artikel_id),
        mitarbeiter_id: form.mitarbeiter_id ? parseInt(form.mitarbeiter_id) : undefined,
        menge: parseFloat(form.menge),
        lagerplatz: form.lagerplatz || undefined,
        notiz: form.notiz || undefined,
      });
      setForm({
        ziel_typ: 'auftrag', auftrag_id: '', projekt_id: '', artikel_id: '',
        mitarbeiter_id: '', menge: '', lagerplatz: '', notiz: '',
      });
      setShowForm(false);
      if (res.data?.bestand_warnung) {
        setWarnung(res.data.bestand_warnung);
      }
      load();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.offene_auftraege) {
        setError(detail.meldung);
        setKandidaten(detail.offene_auftraege);
      } else {
        setError(detail?.meldung || detail || 'Fehler beim Buchen');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    buchen(
      form.ziel_typ === 'auftrag' ? parseInt(form.auftrag_id) : null,
      form.ziel_typ === 'projekt' ? parseInt(form.projekt_id) : null,
    );
  };

  const handleKandidatKlick = (a: AuftragKurz) => {
    setForm(f => ({ ...f, ziel_typ: 'auftrag', auftrag_id: String(a.auftrag_id), projekt_id: '' }));
    buchen(a.auftrag_id, null);
  };

  const toggleForm = () => {
    setError('');
    setKandidaten([]);
    setShowForm(s => !s);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Materialentnahme wirklich löschen? Die Lagerbewegung bleibt bestehen.')) return;
    await axios.delete(`${API_URL}/api/materialentnahmen/${id}`);
    load();
  };

  // Analog zu ZeitbuchungList: projekt_id kann null sein (auftragsgenaue
  // Buchung ohne Baustellen-Bezug) - additiver Anzeige-Fallback statt "#null".
  const projektName = (id: number | null) =>
    id == null ? 'Direkt am Auftrag' : (projekte.find(p => p.projekt_id === id)?.bezeichnung ?? `#${id}`);
  const auftragName = (id: number) => auftraege.find(a => a.auftrag_id === id)?.auftrag_nr ?? `#${id}`;
  const maName = (id?: number) => id ? (mitarbeiter.find(m => m.mitarbeiter_id === id)?.name ?? `#${id}`) : '—';
  const artikelName = (id: number) => artikelListe.find(a => a.artikel_id === id)?.bezeichnung ?? `#${id}`;

  if (loading) return <div style={{ padding: 16 }}>Lade Materialentnahmen…</div>;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Materialentnahmen</h2>
        <button
          className={`k-btn ${showForm ? 'k-btn--ghost' : 'k-btn--primary'}`}
          onClick={toggleForm}
        >
          {showForm ? 'Abbrechen' : '+ Entnahme buchen'}
        </button>
      </div>

      {error && (
        <div style={{ color: 'var(--red, #c0392b)', marginBottom: 12 }}>
          {error}
          {kandidaten.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {kandidaten.map(k => (
                <li key={k.auftrag_id}>
                  <button type="button" className="k-btn k-btn--ghost k-btn--sm" disabled={saving} onClick={() => handleKandidatKlick(k)}>
                    {k.auftrag_nr} – {k.status}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {warnung && (
        <div
          className="k-hinweis k-hinweis--warnung"
          style={{ color: 'var(--amber-ink, #8a6300)', background: 'var(--amber, #fff3cd)', border: '1px solid var(--amber-border, #ffe08a)', padding: '8px 12px', marginBottom: 12 }}
        >
          {warnung}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} style={{ background: 'var(--bone-2)', padding: 16, borderRadius: 0, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <select value={form.ziel_typ} onChange={e => setForm(f => ({ ...f, ziel_typ: e.target.value as 'auftrag' | 'projekt', auftrag_id: '', projekt_id: '' }))} style={{ padding: 8 }}>
              <option value="auftrag">Auf Auftrag buchen</option>
              <option value="projekt">Auf Baustelle buchen</option>
            </select>
            {form.ziel_typ === 'auftrag' ? (
              <select required value={form.auftrag_id} onChange={e => setForm(f => ({ ...f, auftrag_id: e.target.value }))} style={{ padding: 8 }}>
                <option value="">Auftrag wählen *</option>
                {buchbareAuftraege.map(a => <option key={a.auftrag_id} value={a.auftrag_id}>{a.auftrag_nr} – {a.status}</option>)}
              </select>
            ) : (
              <select required value={form.projekt_id} onChange={e => setForm(f => ({ ...f, projekt_id: e.target.value }))} style={{ padding: 8 }}>
                <option value="">Baustelle wählen *</option>
                {projekte.map(p => <option key={p.projekt_id} value={p.projekt_id}>{p.projekt_nr} – {p.bezeichnung}</option>)}
              </select>
            )}
            <select required value={form.artikel_id} onChange={e => setForm(f => ({ ...f, artikel_id: e.target.value }))} style={{ padding: 8 }}>
              <option value="">Artikel wählen *</option>
              {artikelListe.map(a => <option key={a.artikel_id} value={a.artikel_id}>{a.bezeichnung}</option>)}
            </select>
            <input required placeholder="Menge *" type="number" step="0.01" min="0.01" value={form.menge} onChange={e => setForm(f => ({ ...f, menge: e.target.value }))} style={{ padding: 8 }} />
            <select value={form.mitarbeiter_id} onChange={e => setForm(f => ({ ...f, mitarbeiter_id: e.target.value }))} style={{ padding: 8 }}>
              <option value="">Mitarbeiter (optional)</option>
              {mitarbeiter.map(m => <option key={m.mitarbeiter_id} value={m.mitarbeiter_id}>{m.name}</option>)}
            </select>
            <input placeholder="Lagerplatz" value={form.lagerplatz} onChange={e => setForm(f => ({ ...f, lagerplatz: e.target.value }))} style={{ padding: 8 }} />
            <input placeholder="Notiz" value={form.notiz} onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))} style={{ padding: 8 }} />
          </div>
          <button type="submit" className="k-btn k-btn--primary" disabled={saving} style={{ marginTop: 12 }}>
            {saving ? 'Bucht…' : 'Buchen'}
          </button>
        </form>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bone-3)' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Datum</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Auftrag / Baustelle</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Artikel</th>
            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Menge</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Mitarbeiter</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Lagerplatz</th>
            <th style={{ padding: '8px 12px', textAlign: 'left' }}>Notiz</th>
            <th style={{ padding: '8px 12px' }}></th>
          </tr>
        </thead>
        <tbody>
          {entnahmen.map(e => (
            <React.Fragment key={e.entnahme_id}>
              <tr style={{ borderBottom: '1px solid var(--bone-3)' }}>
                <td style={{ padding: '8px 12px' }}>{new Date(e.datum).toLocaleDateString('de-DE')}</td>
                <td style={{ padding: '8px 12px' }}>{e.auftrag_id ? auftragName(e.auftrag_id) : projektName(e.projekt_id)}</td>
                <td style={{ padding: '8px 12px' }}>{artikelName(e.artikel_id)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 500 }}>{e.menge}</td>
                <td style={{ padding: '8px 12px' }}>{maName(e.mitarbeiter_id)}</td>
                <td style={{ padding: '8px 12px', color: 'var(--ink-2)' }}>{e.lagerplatz || '—'}</td>
                <td style={{ padding: '8px 12px', color: 'var(--ink-2)' }}>{e.notiz || '—'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  <button className="k-btn k-btn--ghost k-btn--sm" onClick={() => handleDelete(e.entnahme_id)}>Löschen</button>
                  <button
                    className="k-btn k-btn--ghost k-btn--sm"
                    style={{ marginLeft: 6 }}
                    onClick={() => setAkteId(akteId === e.entnahme_id ? null : e.entnahme_id)}
                  >
                    {akteId === e.entnahme_id ? 'Akte schließen' : 'Akte'}
                  </button>
                </td>
              </tr>
              {akteId === e.entnahme_id && (
                <tr>
                  <td colSpan={8} style={{ background: 'var(--bone-2)', padding: '12px 24px' }}>
                    <AktivitaetenBlock entitaetTyp="materialentnahme" entitaetId={e.entnahme_id} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {entnahmen.length === 0 && (
            <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>Keine Materialentnahmen vorhanden.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MaterialentnahmeList;
